import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { OTPInput } from 'input-otp';
import get from 'lodash/get';
import { useId } from 'react';
import { Controller, type FieldValues, type Path, type RegisterOptions, useFormContext } from 'react-hook-form';
import type { LabelTooltipProps } from '../../label';
import FormWrapper from '../internals/form-wrapper';
export type FormOtpProps<TFieldValues extends FieldValues> = {
  name: Path<TFieldValues>;
  label?: string | React.ReactNode;
  hint?: string;
  rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
  disabled?: boolean;
  autoFocus?: boolean;
  onComplete?: (value: string) => void;
  errorPosition?: 'absolute' | 'relative';
  size?: 'default' | 'compact';
  tooltip?: LabelTooltipProps;
  className?: string;
  containerClassName?: string;
};
type SlotProps = {
  char: string | null;
  isActive: boolean;
  hasFakeCaret: boolean;
  hasError?: boolean;
  size?: 'default' | 'compact';
};
const FormOtp = t0 => {
  const $ = _c(26);
  const {
    name,
    label,
    hint,
    rules,
    disabled,
    autoFocus,
    onComplete,
    errorPosition: t1,
    size: t2,
    tooltip,
    className,
    containerClassName
  } = t0;
  const errorPosition = t1 === undefined ? "relative" : t1;
  const size = t2 === undefined ? "default" : t2;
  const {
    control,
    formState: t3
  } = useFormContext();
  const {
    errors,
    disabled: formDisabled
  } = t3;
  const contextId = useId();
  let t4;
  if ($[0] !== errors || $[1] !== name) {
    t4 = get(errors, name)?.message;
    $[0] = errors;
    $[1] = name;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const error = t4 as string | undefined;
  const isDisabled = disabled || formDisabled;
  let t5;
  if ($[3] !== autoFocus || $[4] !== className || $[5] !== containerClassName || $[6] !== error || $[7] !== isDisabled || $[8] !== onComplete || $[9] !== size) {
    t5 = t6 => {
      const {
        field
      } = t6;
      return <OTPInput data-slot="input-otp" data-1p-ignore={true} data-lpignore="true" maxLength={6} value={field.value || ""} onChange={field.onChange} onComplete={onComplete} disabled={isDisabled} autoFocus={autoFocus} pattern="^[0-9]*$" inputMode="numeric" containerClassName={cx("flex items-center gap-2", isDisabled && "opacity-50 cursor-not-allowed", containerClassName)} className={cx("disabled:cursor-not-allowed", className)} render={t7 => {
        const {
          slots
        } = t7;
        return <OtpGroup>{slots.map((slot, slotIndex) => <Slot key={slotIndex} char={slot.char} isActive={slot.isActive} hasFakeCaret={slot.hasFakeCaret} hasError={!!error} size={size} />)}</OtpGroup>;
      }} />;
    };
    $[3] = autoFocus;
    $[4] = className;
    $[5] = containerClassName;
    $[6] = error;
    $[7] = isDisabled;
    $[8] = onComplete;
    $[9] = size;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== control || $[12] !== name || $[13] !== rules || $[14] !== t5) {
    t6 = <Controller name={name} control={control} rules={rules} render={t5} />;
    $[11] = control;
    $[12] = name;
    $[13] = rules;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== contextId || $[17] !== error || $[18] !== errorPosition || $[19] !== hint || $[20] !== isDisabled || $[21] !== label || $[22] !== size || $[23] !== t6 || $[24] !== tooltip) {
    t7 = <FormWrapper id={contextId} error={error} label={label} errorPosition={errorPosition} size={size} hint={hint} fullWidth={false} disabled={isDisabled} tooltip={tooltip}>{t6}</FormWrapper>;
    $[16] = contextId;
    $[17] = error;
    $[18] = errorPosition;
    $[19] = hint;
    $[20] = isDisabled;
    $[21] = label;
    $[22] = size;
    $[23] = t6;
    $[24] = tooltip;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
};
const Slot = t0 => {
  const $ = _c(11);
  const {
    char,
    isActive,
    hasFakeCaret,
    hasError,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== hasError || $[1] !== isActive || $[2] !== size) {
    t2 = cx("relative flex items-center justify-center", "border border-solid rounded-md", "transition-all duration-200", "bg-primary", {
      "h-10 w-10 text-body-2": size === "default"
    }, {
      "h-9 w-9 text-body-2": size === "compact"
    }, {
      "border-secondary shadow-focus-accent": isActive && !hasError
    }, {
      "border-error shadow-focus-error": isActive && hasError
    }, {
      "border-primary": !isActive && !hasError
    }, {
      "border-error": !isActive && hasError
    });
    $[0] = hasError;
    $[1] = isActive;
    $[2] = size;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== hasFakeCaret) {
    t3 = hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="h-4 w-px bg-[#000] dark:bg-[#fff]" style={{
        animation: "caretBlink 1s ease-out infinite"
      }} /></div>;
    $[4] = hasFakeCaret;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== char || $[7] !== isActive || $[8] !== t2 || $[9] !== t3) {
    t4 = <div data-slot="input-otp-slot" data-active={isActive} className={t2}>{char}{t3}</div>;
    $[6] = char;
    $[7] = isActive;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
const OtpGroup = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cx("flex items-center gap-2", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="input-otp-group" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export default FormOtp;